// Copyright (c) Docugami, Inc. All rights reserved.

import { createSelector } from "@reduxjs/toolkit";
import { IClause, IClauseItem } from "components/assisted_authoring/ClauseList";
import { MissingAnomaly } from "components/assisted_authoring/ClauseSuggestionsList";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { AssistedAuthoringChunkInfo } from "networking/api";
import { Clause, ClauseLabel } from "networking/assistedAuthoringApi";
import { AppState } from "store";
import { selectAllClauseLabels } from "./assistedAuthoringSlice";
import { selectAllClauses } from "./assistedAuthoringSlice";
import { selectGetAllClauses, ClausesState } from "./assistedAuthoringSlice";
import { selectAllAAChunkInfos, selectGetAssistedAuthoringChunkInfos } from "./assistedAuthoringSlice";

function sortMissingAnomaliesByLikelihood(arr: MissingAnomaly[]): MissingAnomaly[] {
    const res = [...arr];

    res.sort((a: MissingAnomaly, b: MissingAnomaly) => {
        if (!a.chunkSuggestion || !a.chunkSuggestion.likelihood ||
            !b.chunkSuggestion || !b.chunkSuggestion.likelihood) {
            return 0;
        }

        if (a.chunkSuggestion.likelihood > b.chunkSuggestion.likelihood) {
            return -1;
        }

        if (a.chunkSuggestion.likelihood === b.chunkSuggestion.likelihood) {
            return 0;
        }

        return 1;
    });

    return res;
}

export const selectMissingAnomalyInstances = createSelector(
    [
        (state: AppState, documentId: string) => selectGetAssistedAuthoringChunkInfos(state, documentId)
    ], 
    (infos): MissingAnomaly[] => {
        if (!infos) {
            return [];
        }

        const missingAnomalies: MissingAnomaly[] = [];
        for (const info of selectAllAAChunkInfos(infos)) {
            if (!info.suggestedChunks) {
                continue;
            }

            for (const chunkSuggestion of info.suggestedChunks) {
                if (chunkSuggestion.likelihood && chunkSuggestion.variants && chunkSuggestion.variants.length > 0) {
                    missingAnomalies.push({
                        containerChunkSpanId: info.spanId,
                        precedingSiblingSpanId: chunkSuggestion.precedingSiblingId ?? "",
                        followingSiblingSpanId: chunkSuggestion.followingSiblingId ?? "",
                        chunkSuggestion: chunkSuggestion
                    });
                }
            }
        }

        return sortMissingAnomaliesByLikelihood(missingAnomalies);
    }
)

export const selectSuggestionsInDocOrder = createSelector(
    [
        (state: AppState, documentId: string) => selectGetAssistedAuthoringChunkInfos(state, documentId),
        (state: AppState, documentId: string) => documentId
    ], 
    (infosState, documentId): AssistedAuthoringChunkInfo[] => {
        if (!infosState || !DgmlInstancesSingleton.isInitialized(documentId)) {
            return [];
        }

        const infos = selectAllAAChunkInfos(infosState);
        infos.sort((a: AssistedAuthoringChunkInfo, b: AssistedAuthoringChunkInfo) => {
            const chunkInstanceA = DgmlInstancesSingleton.getInstanceById(documentId, a.spanId);
            const chunkInstanceB = DgmlInstancesSingleton.getInstanceById(documentId, b.spanId);

            if (!chunkInstanceA!.tokenRange || !chunkInstanceB!.tokenRange) {
                return 0;
            }

            if (chunkInstanceA!.tokenRange.fromTok < chunkInstanceB!.tokenRange.fromTok) {
                return -1;
            }

            if (chunkInstanceA!.tokenRange.fromTok === chunkInstanceB!.tokenRange.fromTok) {
                return 0;
            }

            return 1;
        });

        return infos;
    }
)

export const selectClauseItems = createSelector(
    [
        (state: AppState, docSetId: string) => selectGetAllClauses(state, docSetId)
    ],
    (clausesState?: ClausesState): IClauseItem[] => {
        if (!clausesState) {
            return [];
        }

        const items: IClauseItem[] = [];
        const clauses: Clause[] = selectAllClauses(clausesState.clauses);
        for (const id of clausesState.clauseLabels.ids) {
            const clauseLabel = clausesState.clauseLabels.entities[id];
            if (!!clauseLabel) {
                const clauseLabelClauses = clauses.filter(c => c.clauseLabelId === clauseLabel.id);
                items.push(transformClauseLabelModel(clauseLabel, clauseLabelClauses));
            }
        }

        return items;
    }
)

function transformClauseLabelModel(clauseLabel: ClauseLabel, clauses: Clause[]): IClauseItem {
    return {
        id: clauseLabel.id,
        label: clauseLabel.label,
        variants: clauses.map(transformClauseModel)
    }
}

function transformClauseModel(clause: Clause): IClause {
    return {
        id: clause.id,
        dgml: clause.dgmlContent,
        documentId: clause.folderId
    }
}

export const selectExistingClauseLabels = createSelector(
    [
        (state: AppState, docSetId: string) => selectGetAllClauses(state, docSetId)
    ],
    (clausesState?: ClausesState): string[] => {
        if (!clausesState) {
            return [];
        }

        const clauseLabels: ClauseLabel[] = selectAllClauseLabels(clausesState.clauseLabels);
        return clauseLabels.map(x => x.label.toLocaleLowerCase());
    }
)